import { ReactNode } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";

const { color, typeScale } = adminTokens;
const [, , spaceMd, , spaceLg] = adminTokens.spacing;

// Two columns of equal width; minmax(0, 1fr) lets a column shrink below the
// width of a long unbroken value, which then wraps (FIELDS-R3, FIELDS-R4). On a
// phone the fields stand in one column (PHONE-R29).
const List = styled.dl<{ $isPhone: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isPhone }) =>
    $isPhone ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))"};
  gap: ${spaceMd}px ${spaceLg}px;
  margin: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Label = styled.dt`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
  line-height: ${typeScale.meta.lineHeight};
`;

const Value = styled.dd`
  margin: 0;
  color: ${color.navy};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.labelValue.fontSize};
  line-height: ${typeScale.labelValue.lineHeight};
  overflow-wrap: anywhere;

  /* A linked value keeps the value style rather than a table link's (FIELDS-R5). */
  a {
    font-size: inherit;
  }
`;

export interface FieldListItem {
  label: string;
  // Text, or a link such as an invoice at its payment vendor (FIELDS-R5).
  value: ReactNode;
}

// A label and its value, read together as a term and its description
// (FIELDS-R1…R5).
interface FieldListProps {
  fields: FieldListItem[];
}

export const FieldList = ({ fields }: FieldListProps) => {
  const isPhone = useIsPhoneLayout();

  return (
    <List $isPhone={isPhone}>
      {fields.map(({ label, value }, position) => (
        <Field key={`${position}-${label}`}>
          <Label>{label}</Label>
          <Value>{value}</Value>
        </Field>
      ))}
    </List>
  );
};
